import { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { 
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent
} from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { usersApi, rolesApi, permissionsApi, organizationsApi, adminApi } from "@/services/api-v2.service";
import { User, Role, Organization, Permission, RoleCreate, RoleUpdate, UserUpdate, RegisterRequest } from "@/types/api-v2";
import { buildOrganizationsHierarchy, flattenOrganizationsHierarchy } from "@/utils/organizationsHierarchy";
import {
  User as UserIcon, 
  ShieldCheck as ShieldCheckIcon,
  UserCog as UserCogIcon,
  Pencil as PencilIcon,
  Trash2 as TrashIcon,
  ToggleLeft as ToggleLeftIcon,
  ToggleRight as ToggleRightIcon,
} from "lucide-react";

export default function UserManagement() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // Состояния для управления пользователями и ролями
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [roleForm, setRoleForm] = useState({ name: "", description: "" });
  const [userForm, setUserForm] = useState({ email: "", username: "", firstName: "", lastName: "", password: "", organizationId: "", roleId: "" });
  const [isRoleDialogOpen, setIsRoleDialogOpen] = useState(false);
  const [isUserDialogOpen, setIsUserDialogOpen] = useState(false);
  const [isPermissionsDialogOpen, setIsPermissionsDialogOpen] = useState(false);
  const [isEditUserMode, setIsEditUserMode] = useState(false);
  const [isConfirmRoleDeleteOpen, setIsConfirmRoleDeleteOpen] = useState(false);
  const [isConfirmUserDeleteOpen, setIsConfirmUserDeleteOpen] = useState(false);
  const [isConfirmMultiDeleteOpen, setIsConfirmMultiDeleteOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [selectedPermissions, setSelectedPermissions] = useState<number[]>([]);
  const [userFormErrors, setUserFormErrors] = useState<Record<string, string>>({});
  
  // Запросы данных для пользователей и ролей
  const { data: users = [], isLoading: isLoadingUsers } = useQuery<User[]>({
    queryKey: ['users'],
    queryFn: () => adminApi.getAllUsers(),
  });
  
  const { data: roles = [], isLoading: isLoadingRoles } = useQuery<Role[]>({
    queryKey: ['roles'],
    queryFn: () => rolesApi.list(),
  });
  
  const { data: organizations = [] } = useQuery<Organization[]>({
    queryKey: ['organizations'],
    queryFn: () => organizationsApi.list(),
  });
  
  // Иерархия организаций (из KLS, qual_id = 336)
  const organizationsHierarchy = useMemo(() => {
    if (!organizations || organizations.length === 0) return [];
    const hierarchy = buildOrganizationsHierarchy(organizations);
    return flattenOrganizationsHierarchy(hierarchy);
  }, [organizations]);
  
  const { data: permissions = [] } = useQuery<Permission[]>({
    queryKey: ['permissions'],
    queryFn: () => permissionsApi.list(),
  });
  
  // Функции для работы с ролями
  const openRoleDialog = (role?: Role) => {
    if (role) {
      setSelectedRole(role);
      setRoleForm({
        name: role.name,
        description: role.description || "",
      });
    } else {
      setSelectedRole(null);
      setRoleForm({
        name: "",
        description: "",
      });
    }
    setIsRoleDialogOpen(true);
  };

  const openPermissionsDialog = (role: Role) => {
    setSelectedRole(role);
    setIsPermissionsDialogOpen(true);
  };
  
  const openUserDialog = (user?: User) => {
    if (user) {
      // Режим редактирования
      setUserForm({
        email: user.email || "",
        username: user.username || "",
        firstName: user.first_name || "",
        lastName: user.last_name || "",
        password: "", // Поле пароля пустое при редактировании
        organizationId: user.organization_id?.toString() || "",
        roleId: user.role_id?.toString() || ""
      });
      setIsEditUserMode(true);
      setSelectedUserId(user.id);
    } else {
      // Режим создания
      setUserForm({
        email: "",
        username: "",
        firstName: "",
        lastName: "",
        password: "",
        organizationId: "",
        roleId: ""
      });
      setIsEditUserMode(false);
      setSelectedUserId(null);
    }
    setUserFormErrors({});
    setIsUserDialogOpen(true);
  };
  
  // Валидация формы пользователя
  const validateUserForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    if (!isEditUserMode) {
      // При создании пароль обязателен
      if (!userForm.password) {
        errors.password = "Пароль обязателен";
      } else if (userForm.password.length < 8) {
        errors.password = "Пароль должен содержать минимум 8 символов";
      }
      
      // Email или username обязательны
      if (!userForm.email && !userForm.username) {
        errors.email = "Email или имя пользователя обязательны";
        errors.username = "Email или имя пользователя обязательны";
      }
    }
    
    // Валидация username (если указан)
    if (userForm.username) {
      if (userForm.username.length < 3) {
        errors.username = "Имя пользователя должно содержать минимум 3 символа";
      } else if (userForm.username.length > 50) {
        errors.username = "Имя пользователя не должно превышать 50 символов";
      }
    }
    
    // Валидация пароля при редактировании (если указан)
    if (isEditUserMode && userForm.password) {
      if (userForm.password.length < 8) {
        errors.password = "Пароль должен содержать минимум 8 символов";
      }
    }
    
    // Валидация email (если указан)
    if (userForm.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(userForm.email)) {
        errors.email = "Некорректный формат email";
      }
    }
    
    setUserFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  const handleSubmitRole = async () => {
    try {
      if (selectedRole) {
        // Обновление существующей роли
        const updateData: RoleUpdate = {
          name: roleForm.name,
          description: roleForm.description || undefined,
        };
        await rolesApi.update(selectedRole.id, updateData);
        toast({
          title: "Роль обновлена",
          description: `Роль "${roleForm.name}" успешно обновлена`
        });
      } else {
        // Создание новой роли
        const createData: RoleCreate = {
          name: roleForm.name,
          description: roleForm.description || undefined,
        };
        await rolesApi.create(createData);
        toast({
          title: "Роль создана",
          description: `Роль "${roleForm.name}" успешно создана`
        });
      }
      // Обновление списка ролей
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      setIsRoleDialogOpen(false);
    } catch (error) {
      toast({
        title: "Ошибка",
        description: "Не удалось сохранить роль",
        variant: "destructive"
      });
    }
  };
  
  const confirmDeleteRole = (id: number, name: string) => {
    setSelectedRole({ id, name } as Role);
    setIsConfirmRoleDeleteOpen(true);
  };
  
  const confirmDeleteUser = (id: string, name: string) => {
    setSelectedUserId(id);
    setSelectedUser({ id, email: name } as User);
    setIsConfirmUserDeleteOpen(true);
  };
  
  const handleDeleteUser = async () => {
    if (!selectedUserId) return;
    
    try {
      await adminApi.deleteUser(selectedUserId);
      
      // Обновление списка пользователей
      queryClient.invalidateQueries({ queryKey: ['users'] });
      
      setIsConfirmUserDeleteOpen(false);
      
      toast({
        title: "Пользователь удален",
        description: "Пользователь успешно удален из системы"
      });
    } catch (error) {
      console.error("Error deleting user:", error);
      toast({
        title: "Ошибка",
        description: "Не удалось удалить пользователя",
        variant: "destructive"
      });
    }
  };
  
  const toggleSelectUser = (userId: string) => {
    setSelectedUserIds(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId) 
        : [...prev, userId]
    );
  };
  
  const toggleSelectAllUsers = () => {
    if (selectedUserIds.length === users.length) {
      setSelectedUserIds([]);
    } else {
      setSelectedUserIds(users.map(user => user.id));
    }
  };
  
  const confirmDeleteMultipleUsers = () => {
    if (selectedUserIds.length === 0) return;
    setIsConfirmMultiDeleteOpen(true);
  };
  
  const handleDeleteMultipleUsers = async () => {
    if (selectedUserIds.length === 0) return;
    
    try {
      // Последовательное удаление всех выбранных пользователей
      const promises = selectedUserIds.map(userId => 
        adminApi.deleteUser(userId)
      );
      
      await Promise.all(promises);
      
      // Обновление списка пользователей
      queryClient.invalidateQueries({ queryKey: ['users'] });
      
      setIsConfirmMultiDeleteOpen(false);
      setSelectedUserIds([]);
      
      toast({
        title: "Пользователи удалены",
        description: `Успешно удалено ${selectedUserIds.length} пользователей`
      });
    } catch (error) {
      console.error("Error deleting multiple users:", error);
      toast({
        title: "Ошибка",
        description: "Не удалось удалить пользователей",
        variant: "destructive"
      });
    }
  };
  
  const handleDeleteRole = async () => {
    if (!selectedRole) return;
    
    try {
      await rolesApi.delete(selectedRole.id);
      toast({
        title: "Роль удалена",
        description: `Роль "${selectedRole.name}" успешно удалена`
      });
      // Обновление списка ролей
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      setIsConfirmRoleDeleteOpen(false);
    } catch (error: any) {
      toast({
        title: "Ошибка",
        description: error.message || "Не удалось удалить роль",
        variant: "destructive"
      });
    }
  };
  
  // Функции для управления пользователями
  const handleUpdateUserRole = async (userId: string, roleId: number | null) => {
    try {
      const updateData: UserUpdate = { role_id: roleId || undefined };
      await usersApi.update(userId, updateData);
      // Обновление списка пользователей
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast({
        title: "Роль пользователя обновлена",
        description: "Роль пользователя успешно обновлена"
      });
    } catch (error) {
      toast({
        title: "Ошибка",
        description: "Не удалось обновить роль пользователя",
        variant: "destructive"
      });
    }
  };
  
  const handleUpdateUserOrganization = async (userId: string, organizationId: number | null) => {
    try {
      const updateData: UserUpdate = { organization_id: organizationId || undefined };
      await usersApi.update(userId, updateData);
      // Обновление списка пользователей
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast({
        title: "Орган управления пользователя обновлён",
        description: "Орган управления пользователя успешно обновлён"
      });
    } catch (error) {
      toast({
        title: "Ошибка",
        description: "Не удалось обновить орган управления пользователя",
        variant: "destructive"
      });
    }
  };
  
  const handleToggleUserActive = async (userId: string, active: boolean) => {
    try {
      const updateData: UserUpdate = { active };
      await usersApi.update(userId, updateData);
      // Обновление списка пользователей
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast({
        title: active ? "Пользователь активирован" : "Пользователь деактивирован",
        description: active 
          ? "Пользователь успешно активирован" 
          : "Пользователь успешно деактивирован"
      });
    } catch (error) {
      toast({
        title: "Ошибка",
        description: "Не удалось изменить статус пользователя",
        variant: "destructive"
      });
    }
  };
  
  const handleSavePermissions = async () => {
    try {
      if (!selectedRole) return;

      const updateData: RoleUpdate = {
        permission_ids: selectedPermissions,
      };
      await rolesApi.update(selectedRole.id, updateData);

      // Обновляем кэш
      queryClient.invalidateQueries({ queryKey: ['roles', selectedRole.id] });
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      
      toast({
        title: "Разрешения обновлены",
        description: `Разрешения для роли "${selectedRole.name}" успешно обновлены`
      });
      
      setIsPermissionsDialogOpen(false);
    } catch (error) {
      toast({
        title: "Ошибка",
        description: "Не удалось обновить разрешения роли",
        variant: "destructive"
      });
    }
  };

  const handlePermissionToggle = (permissionId: number) => {
    setSelectedPermissions(prev => 
      prev.includes(permissionId)
        ? prev.filter(id => id !== permissionId)
        : [...prev, permissionId]
    );
  };

  const handleSubmitUser = async () => {
    // Валидация формы перед отправкой
    if (!validateUserForm()) {
      toast({
        title: "Ошибка валидации",
        description: "Пожалуйста, исправьте ошибки в форме",
        variant: "destructive"
      });
      return;
    }
    
    try {
      if (isEditUserMode && selectedUserId) {
        // Если пароль пустой, не отправляем его при обновлении
        const updateData: UserUpdate = {
          email: userForm.email || undefined,
          username: userForm.username || undefined,
          first_name: userForm.firstName || undefined,
          last_name: userForm.lastName || undefined,
          organization_id: userForm.organizationId && userForm.organizationId !== "null" 
            ? parseInt(userForm.organizationId) 
            : undefined,
          role_id: userForm.roleId && userForm.roleId !== "null" 
            ? parseInt(userForm.roleId) 
            : undefined,
          ...(userForm.password && { password: userForm.password }),
        };
        
        await usersApi.update(selectedUserId, updateData);
        
        toast({
          title: "Пользователь обновлен",
          description: `Пользователь успешно обновлен`
        });
      } else {
        const createData: RegisterRequest = {
          email: userForm.email || undefined,
          username: userForm.username || undefined,
          password: userForm.password,
          first_name: userForm.firstName || undefined,
          last_name: userForm.lastName || undefined,
          organization_id: userForm.organizationId && userForm.organizationId !== "null"
            ? parseInt(userForm.organizationId, 10)
            : undefined,
          role_id: userForm.roleId && userForm.roleId !== "null"
            ? parseInt(userForm.roleId, 10)
            : undefined,
          active: true,
        };

        await usersApi.create(createData);
        
        toast({
          title: "Пользователь создан",
          description: `Пользователь успешно создан`
        });
      }
      
      // Обновление списка пользователей
      queryClient.invalidateQueries({ queryKey: ['users'] });
      
      setIsUserDialogOpen(false);
      setUserFormErrors({});
    } catch (error: any) {
      console.error("Error handling user:", error);
      
      // Обработка ошибок валидации от API
      if (error?.message && error.message.includes("validation")) {
        const errorMessage = error.message;
        toast({
          title: "Ошибка валидации",
          description: errorMessage,
          variant: "destructive"
        });
      } else {
        toast({
          title: "Ошибка",
          description: isEditUserMode 
            ? "Не удалось обновить пользователя" 
            : "Не удалось создать пользователя",
          variant: "destructive"
        });
      }
    }
  };
  
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-semibold">Управление пользователями и правами</h3>
        <div className="flex space-x-2">
          <Button onClick={() => openUserDialog()}>
            <UserIcon className="h-4 w-4 mr-1" /> Добавить пользователя
          </Button>
          <Button onClick={() => openRoleDialog()}>
            <ShieldCheckIcon className="h-4 w-4 mr-1" /> Добавить роль
          </Button>
        </div>
      </div>
      
      <Tabs defaultValue="users" className="w-full">
        <TabsList className="border-b px-4">
          <TabsTrigger value="users" className="flex items-center gap-2">
            <UserIcon className="h-4 w-4" /> Пользователи
          </TabsTrigger>
          <TabsTrigger value="roles" className="flex items-center gap-2">
            <ShieldCheckIcon className="h-4 w-4" /> Роли
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="users" className="p-4">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-medium">Управление пользователями</h3>
              {selectedUserIds.length > 0 && (
                <Badge variant="outline" className="ml-2">
                  Выбрано: {selectedUserIds.length}
                </Badge>
              )}
            </div>
            <div className="flex gap-2">
              {selectedUserIds.length > 0 && (
                <Button 
                  onClick={confirmDeleteMultipleUsers} 
                  variant="destructive"
                  size="sm"
                >
                  <TrashIcon className="h-4 w-4 mr-2" />
                  Удалить выбранных
                </Button>
              )}
            </div>
          </div>
          {isLoadingUsers ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full"></div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox 
                      checked={selectedUserIds.length > 0 && selectedUserIds.length === users.length}
                      onCheckedChange={toggleSelectAllUsers}
                      aria-label="Выбрать всех"
                    />
                  </TableHead>
                  <TableHead className="w-1/8">ID</TableHead>
                  <TableHead className="w-1/8">Email</TableHead>
                  <TableHead className="w-1/8">Логин</TableHead>
                  <TableHead className="w-1/8">Имя</TableHead>
                  <TableHead className="w-1/8">Фамилия</TableHead>
                  <TableHead className="w-1/8">Роль</TableHead>
                  <TableHead className="w-1/8">Орган управления</TableHead>
                  <TableHead className="w-1/8">Статус</TableHead>
                  <TableHead className="w-24">Действия</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-4">Нет данных</TableCell>
                  </TableRow>
                ) : (
                  users.map(user => (
                    <TableRow key={user.id}>
                      <TableCell>
                        <Checkbox 
                          checked={selectedUserIds.includes(user.id)}
                          onCheckedChange={() => toggleSelectUser(user.id)}
                          aria-label={`Выбрать пользователя ${user.email || user.username}`}
                        />
                      </TableCell>
                      <TableCell className="font-mono text-xs">{user.id}</TableCell>
                      <TableCell>{user.email || "-"}</TableCell>
                      <TableCell>{user.username || "-"}</TableCell>
                      <TableCell>{user.first_name || "-"}</TableCell>
                      <TableCell>{user.last_name || "-"}</TableCell>
                      <TableCell>
                        <Select 
                          defaultValue={user.role_id?.toString() || "null"} 
                          onValueChange={(value) => handleUpdateUserRole(user.id, value !== "null" ? parseInt(value, 10) : null)}
                        >
                          <SelectTrigger className="w-32">
                            <SelectValue placeholder="Выберите роль" />
                          </SelectTrigger>
                          <SelectContent className="z-[9999]">
                            <SelectItem value="null">Нет роли</SelectItem>
                            {roles.map(role => (
                              <SelectItem key={role.id} value={role.id.toString()}>
                                {role.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Select 
                          defaultValue={user.organization_id?.toString() || "null"} 
                          onValueChange={(value) => handleUpdateUserOrganization(user.id, value !== "null" ? parseInt(value, 10) : null)}
                        >
                          <SelectTrigger className="w-36">
                            <SelectValue placeholder="Выберите орган управления" />
                          </SelectTrigger>
                          <SelectContent className="z-[9999]">
                            <SelectItem value="null">Нет органа управления</SelectItem>
                            {organizationsHierarchy.length > 0 
                              ? organizationsHierarchy.map(org => {
                                  const paddingLeft = org.indentLevel * 16;
                                  const prefix = org.indentLevel > 0 ? '└─ ' : '';
                                  // Используем краткое название если есть
                                  const displayName = org.name_short || org.name;
                                  return (
                                    <SelectItem 
                                      key={org.id} 
                                      value={org.id.toString()}
                                      style={{ paddingLeft: `${paddingLeft + 8}px` }}
                                      title={org.name}
                                    >
                                      {prefix}{displayName}
                                    </SelectItem>
                                  );
                                })
                              : organizations.map(org => {
                                  const displayName = org.name_short || org.name;
                                  return (
                                    <SelectItem key={org.id} value={org.id.toString()} title={org.name}>
                                      {displayName}
                                    </SelectItem>
                                  );
                                })
                            }
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleToggleUserActive(user.id, !user.active)}
                          className={user.active ? "text-green-600" : "text-red-600"}
                        >
                          {user.active ? (
                            <ToggleRightIcon className="h-5 w-5 mr-1" />
                          ) : (
                            <ToggleLeftIcon className="h-5 w-5 mr-1" />
                          )}
                          {user.active ? "Активен" : "Неактивен"}
                        </Button>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => openUserDialog(user)}
                            title="Редактировать"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="text-red-600" 
                            onClick={() => confirmDeleteUser(user.id, user.email || user.username || "")}
                            title="Удалить"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </TabsContent>
        
        <TabsContent value="roles" className="p-4">
          <h3 className="text-lg font-medium mb-4">Управление ролями</h3>
          {isLoadingRoles ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full"></div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>Название</TableHead>
                  <TableHead>Описание</TableHead>
                  <TableHead className="w-24">Действия</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {roles.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-4">Нет данных</TableCell>
                  </TableRow>
                ) : (
                  roles.map(role => (
                    <TableRow key={role.id}>
                      <TableCell>{role.id}</TableCell>
                      <TableCell className="font-medium">{role.name}</TableCell>
                      <TableCell>{role.description || "-"}</TableCell>
                      <TableCell className="flex gap-2">
                        <Button variant="ghost" size="icon" onClick={() => openRoleDialog(role)}>
                          <PencilIcon className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="text-blue-600" onClick={() => openPermissionsDialog(role)}>
                          <UserCogIcon className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="text-red-600" onClick={() => confirmDeleteRole(role.id, role.name)}>
                          <TrashIcon className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </TabsContent>
      </Tabs>
      
      {/* Диалог создания/редактирования роли */}
      <Dialog
        open={isRoleDialogOpen}
        onOpenChange={setIsRoleDialogOpen}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{selectedRole ? "Редактирование роли" : "Создание новой роли"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-3">
            <div className="space-y-2">
              <Label htmlFor="role-name">Название роли</Label>
              <Input 
                id="role-name" 
                value={roleForm.name} 
                onChange={e => setRoleForm({...roleForm, name: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="role-description">Описание</Label>
              <Textarea 
                id="role-description" 
                value={roleForm.description} 
                onChange={e => setRoleForm({...roleForm, description: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRoleDialogOpen(false)}>Отмена</Button>
            <Button onClick={handleSubmitRole}>Сохранить</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Диалог подтверждения удаления роли */}
      <ConfirmDialog
        isOpen={isConfirmRoleDeleteOpen}
        title="Удаление роли"
        description={`Вы уверены, что хотите удалить роль "${selectedRole?.name}"?`}
        onConfirm={handleDeleteRole}
        onCancel={() => setIsConfirmRoleDeleteOpen(false)}
      />
      
      {/* Диалог подтверждения удаления пользователя */}
      <ConfirmDialog
        isOpen={isConfirmUserDeleteOpen}
        title="Удаление пользователя"
        description={`Вы уверены, что хотите удалить пользователя "${selectedUser?.email || ''}"?`}
        onConfirm={handleDeleteUser}
        onCancel={() => setIsConfirmUserDeleteOpen(false)}
      />
      
      {/* Диалог подтверждения массового удаления пользователей */}
      <ConfirmDialog
        isOpen={isConfirmMultiDeleteOpen}
        title="Массовое удаление пользователей"
        description={`Вы уверены, что хотите удалить ${selectedUserIds.length} пользователей? Это действие нельзя отменить.`}
        onConfirm={handleDeleteMultipleUsers}
        onCancel={() => setIsConfirmMultiDeleteOpen(false)}
      />
      
      {/* Диалог создания нового пользователя */}
      <Dialog open={isUserDialogOpen} onOpenChange={setIsUserDialogOpen}>
        <DialogContent className="max-w-md z-[1000]">
          <DialogHeader>
            <DialogTitle>{isEditUserMode ? "Редактирование пользователя" : "Создание нового пользователя"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="user-email">
                Email {!isEditUserMode && <span className="text-muted-foreground text-xs">(или имя пользователя)</span>}
              </Label>
              <Input
                id="user-email"
                type="email"
                value={userForm.email}
                onChange={(e) => {
                  setUserForm({ ...userForm, email: e.target.value });
                  if (userFormErrors.email) {
                    setUserFormErrors({ ...userFormErrors, email: "" });
                  }
                }}
                placeholder="user@example.com"
                className={userFormErrors.email ? "border-red-500" : ""}
              />
              {userFormErrors.email && (
                <p className="text-sm text-red-500">{userFormErrors.email}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="user-username">
                Имя пользователя (логин) {!isEditUserMode && <span className="text-muted-foreground text-xs">(или email)</span>}
              </Label>
              <Input
                id="user-username"
                value={userForm.username}
                onChange={(e) => {
                  setUserForm({ ...userForm, username: e.target.value });
                  if (userFormErrors.username) {
                    setUserFormErrors({ ...userFormErrors, username: "" });
                  }
                }}
                placeholder="Минимум 3 символа"
                className={userFormErrors.username ? "border-red-500" : ""}
              />
              {userFormErrors.username && (
                <p className="text-sm text-red-500">{userFormErrors.username}</p>
              )}
              {!userFormErrors.username && userForm.username && userForm.username.length < 3 && (
                <p className="text-sm text-muted-foreground">
                  Осталось символов: {3 - userForm.username.length}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="user-firstName">Имя</Label>
              <Input
                id="user-firstName"
                value={userForm.firstName}
                onChange={(e) => setUserForm({ ...userForm, firstName: e.target.value })}
                placeholder="Введите имя пользователя"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="user-lastName">Фамилия</Label>
              <Input
                id="user-lastName"
                value={userForm.lastName}
                onChange={(e) => setUserForm({ ...userForm, lastName: e.target.value })}
                placeholder="Введите фамилию пользователя"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="user-password">
                Пароль {!isEditUserMode && <span className="text-muted-foreground text-xs">(обязательно)</span>}
                {isEditUserMode && <span className="text-muted-foreground text-xs">(оставьте пустым, чтобы не менять)</span>}
              </Label>
              <Input
                id="user-password"
                type="password"
                value={userForm.password}
                onChange={(e) => {
                  setUserForm({ ...userForm, password: e.target.value });
                  if (userFormErrors.password) {
                    setUserFormErrors({ ...userFormErrors, password: "" });
                  }
                }}
                placeholder={isEditUserMode ? "Оставьте пустым, чтобы не менять" : "Минимум 8 символов"}
                className={userFormErrors.password ? "border-red-500" : ""}
              />
              {userFormErrors.password && (
                <p className="text-sm text-red-500">{userFormErrors.password}</p>
              )}
              {!userFormErrors.password && userForm.password && userForm.password.length < 8 && !isEditUserMode && (
                <p className="text-sm text-muted-foreground">
                  Осталось символов: {8 - userForm.password.length}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="user-organization">Орган управления</Label>
              <Select 
                value={userForm.organizationId || "null"} 
                onValueChange={(value) => setUserForm({ ...userForm, organizationId: value === "null" ? "" : value })}
              >
                <SelectTrigger id="user-organization">
                  <SelectValue placeholder="Выберите орган управления" />
                </SelectTrigger>
                <SelectContent className="z-[2000]">
                  <SelectItem value="null">Не выбрано</SelectItem>
                  {organizationsHierarchy.length > 0 
                    ? organizationsHierarchy.map(org => {
                        const paddingLeft = org.indentLevel * 16;
                        const prefix = org.indentLevel > 0 ? '└─ ' : '';
                        // Используем краткое название если есть
                        const displayName = org.name_short || org.name;
                        return (
                          <SelectItem 
                            key={org.id} 
                            value={String(org.id)}
                            style={{ paddingLeft: `${paddingLeft + 8}px` }}
                            title={org.name}
                          >
                            {prefix}{displayName}
                          </SelectItem>
                        );
                      })
                    : organizations.map((org) => {
                        const displayName = org.name_short || org.name;
                        return (
                          <SelectItem key={org.id} value={String(org.id)} title={org.name}>
                            {displayName}
                          </SelectItem>
                        );
                      })
                  }
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="user-role">Роль</Label>
              <Select 
                value={userForm.roleId || "null"} 
                onValueChange={(value) => setUserForm({ ...userForm, roleId: value === "null" ? "" : value })}
              >
                <SelectTrigger id="user-role">
                  <SelectValue placeholder="Выберите роль" />
                </SelectTrigger>
                <SelectContent className="z-[2000]">
                  <SelectItem value="null">Не выбрано</SelectItem>
                  {roles.map((role) => (
                    <SelectItem key={role.id} value={String(role.id)}>
                      {role.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsUserDialogOpen(false)}
            >
              Отмена
            </Button>
            <Button
              onClick={handleSubmitUser}
              disabled={
                (!userForm.email && !userForm.username) || 
                (!userForm.password && !isEditUserMode)
              }
            >
              {isEditUserMode ? "Сохранить" : "Создать пользователя"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Диалог управления разрешениями роли */}
      <Dialog
        open={isPermissionsDialogOpen}
        onOpenChange={setIsPermissionsDialogOpen}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              Управление разрешениями роли: {selectedRole?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="text-sm text-muted-foreground">
              Выберите разрешения, которые должны быть у этой роли:
            </div>
            
            {permissions.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                Разрешения не найдены
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3 max-h-96 overflow-y-auto">
                {permissions.map((permission) => (
                  <div key={permission.id} className="flex items-center space-x-3 p-3 border rounded-lg">
                    <Checkbox
                      id={`permission-${permission.id}`}
                      checked={selectedPermissions.includes(permission.id)}
                      onCheckedChange={() => handlePermissionToggle(permission.id)}
                    />
                    <div className="flex-1">
                      <label 
                        htmlFor={`permission-${permission.id}`}
                        className="text-sm font-medium cursor-pointer"
                      >
                        {permission.name}
                      </label>
                      {permission.description && (
                        <div className="text-xs text-muted-foreground mt-1">
                          {permission.description}
                        </div>
                      )}
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {permission.category || 'Общие'}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPermissionsDialogOpen(false)}>
              Отмена
            </Button>
            <Button onClick={handleSavePermissions}>
              Сохранить разрешения
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}